import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { useRegisterMutation } from '../slices/usersApiSlice';
import { useSendBeaumontCodeMutation, useConfirmBeaumontCodeMutation } from '../slices/usersApiSlice';
import { toast } from 'react-toastify';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice.js';
import { logout } from '../slices/authSlice';
import { CATEGORY_OF_STAFF } from '../utils/enums';


const dropdownStyle = { backgroundColor: '#F5F5F5' };
const textStyle     = { backgroundColor: '#f5f5dc' };



// ─── OTP digit input (same pattern as LoginScreen) ───────────────────────────
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const digits    = value.split('');

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
      } else if (index > 0) {
        next[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }
      onChange(next.join(''));
    }
  };

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = char;
    onChange(next.join(''));
    if (char && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '12px 0' }}>
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          style={{
            width: 44,
            height: 52,
            border: `2px solid ${digits[i] ? '#1976d2' : '#ccc'}`,
            borderRadius: 8,
            fontSize: '1.6rem',
            fontWeight: 700,
            textAlign: 'center',
            fontFamily: 'monospace',
            backgroundColor: digits[i] ? '#e3f2fd' : '#fff',
            color: '#1976d2',
            outline: 'none',
          }}
        />
      ))}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const RegisterScreen = () => {
  // ── Beaumont verification state ───────────────────────────────────────────
  const [beaumontEmail,     setBeaumontEmail]     = useState('');
  const [beaumontOtp,       setBeaumontOtp]       = useState('');
  const [beaumontVerified,  setBeaumontVerified]  = useState(false);
  const [codeSent,          setCodeSent]          = useState(false);
  const [resendCooldown,    setResendCooldown]    = useState(0);

  const [sendBeaumontCode,    { isLoading: sendingCode }]    = useSendBeaumontCodeMutation();
  const [confirmBeaumontCode, { isLoading: confirmingCode }] = useConfirmBeaumontCodeMutation();

  // ── Registration form state ───────────────────────────────────────────────
  const [name,            setName]            = useState('-');
  const [email,           setEmail]           = useState('');
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstname,       setFirstname]       = useState('');
  const [lastname,        setLastname]        = useState('');
  const [employeenumber,  setEmployeenumber]  = useState('');
  const [jobtitle,        setJobtitle]        = useState('');
  const [salutation,      setSalutation]      = useState('--');
  const [comment,         setComment]         = useState('--');

  //const [effectiveDate,   setEffectiveDate]   = useState(new Date());
  //const [reviewDate,      setReviewDate]      = useState(new Date());
  //default to today and 1 year from today as ISO date strings
const today    = new Date().toISOString().slice(0, 10);
const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                   .toISOString().slice(0, 10);

const [effectiveDate, setEffectiveDate] = useState(today);
const [reviewDate,    setReviewDate]    = useState(nextYear);


  const [openingBalance,  setOpeningBalance]  = useState(0);
  const [bringForward,    setBringForward]    = useState(0);


  const [speciality,            setSpeciality]            = useState('');
  const [specialityID,          setSpecialityID]          = useState('');
  const [directoratedepartment, setDirectoratedepartment] = useState('');
  const [directorateID,         setDirectorateID]         = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();

  const { data: hospitalLocations, isLoading: isLoadingHL, error: errorHL } =
    useGetHospitalLocationsQuery();

  const { userInfo } = useSelector((state) => state.auth);
  const { search }   = useLocation();
  const redirect     = new URLSearchParams(search).get('redirect') || '/';
// In RegisterScreen — add to state
const [registrationToken, setRegistrationToken] = useState('');


  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [navigate, redirect, userInfo]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Auto-confirm when all 6 digits entered
  useEffect(() => {
    if (codeSent && !beaumontVerified && beaumontOtp.length === 6) {
      handleConfirmCode();
    }
  }, [beaumontOtp]);

  const locationsByDirectorate = (hospitalLocations ?? []).reduce((acc, loc) => {
    if (!acc[loc.directorate]) acc[loc.directorate] = [];
    acc[loc.directorate].push(loc);
    return acc;
  }, {});
  const directorates = Object.keys(locationsByDirectorate).sort();

  // ── Beaumont verification handlers ────────────────────────────────────────
  const handleSendCode = async () => {
    if (!beaumontEmail.trim()) {
      toast.error('Please enter your Beaumont email address');
      return;
    }
    
    if (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com')) {
      toast.error('Only @beaumont.ie or @beaumontie.onmicrosoft.com email addresses are accepted for verification.');
      return;
    }
    try {
      await sendBeaumontCode({ beaumontEmail: beaumontEmail.trim() }).unwrap();
      setCodeSent(true);
      setBeaumontOtp('');
      setResendCooldown(60);
      toast.info('A 6-digit code has been sent to your Beaumont email.', { autoClose: 5000 });
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Could not send code');
    }
  };

  const handleConfirmCode = async () => {
    if (beaumontOtp.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    try {
      const result = await confirmBeaumontCode({
        beaumontEmail: beaumontEmail.trim(),
        otp: beaumontOtp,
      }).unwrap();

      setBeaumontVerified(true);
      setRegistrationToken(result.registrationToken);  // ← store it

      toast.success('Beaumont email verified ✓');
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Invalid or expired code');
      setBeaumontOtp('');
    }
  };

  // ── Registration submit ───────────────────────────────────────────────────
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!beaumontVerified) {
      toast.error('Please verify your Beaumont email address before registering');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await register({
        name: `${firstname} ${lastname}`,
        email,
        password,
        employeenumber,
        firstname,
        lastname,
        jobtitle,
        speciality,
        specialityID,
        directoratedepartment,
        directorateID,
        effectiveDate,
        reviewDate,
        comment,
        salutation,
        openingBalance,
        bringForward,
        registrationToken,  // ← backend will verify this
      }).unwrap();

      dispatch(logout());
      toast.success('Registration successful. Please log in once your account is approved by Admin.');
      navigate('/login?message=registration-pending');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoadingHL) return <Loader />;

  if (errorHL) {
    const isAuthError =
      errorHL?.status === 401 ||
      errorHL?.data?.message?.toLowerCase().includes('not authorized');
    return (
      <Container fluid className="mt-4">
        <Message variant="danger">
          {isAuthError ? 'Could not load location data.' : errorHL?.data?.message || 'Error loading hospital locations'}
        </Message>
        <Row className="py-3">
          <Col>
            Already have an account?{' '}
            <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1>Register</h1>

      {/* ── Beaumont Email Verification Panel ── */}
      <div style={{
        border: beaumontVerified ? '2px solid #4caf50' : '2px solid #1976d2',
        borderRadius: 10,
        padding: 20,
        marginBottom: 24,
        backgroundColor: beaumontVerified ? '#f1f8f1' : '#e3f2fd',
      }}>
        <h5 style={{ marginTop: 0, color: beaumontVerified ? '#2e7d32' : '#1976d2' }}>
          {beaumontVerified
            ? '✓ Beaumont Email Verified'
            : 'Step 1: Verify your Beaumont Hospital email'}
        </h5>

        {beaumontVerified ? (
          <p style={{ margin: 0, color: '#2e7d32', fontWeight: 500 }}>
            Verified as: <strong>{beaumontEmail}</strong>
          </p>
        ) : (
          <>
          <p style={{ color: '#555', fontSize: 14, marginBottom: 12 }}>
            You must verify a <strong>@beaumont.ie</strong> or{' '}
            <strong>@beaumontie.onmicrosoft.com</strong> email address before completing
            registration.
          </p>
          
            <Row className="align-items-end g-2">
              <Col md={6}>
                <Form.Group controlId="beaumontEmail">
                  <Form.Label>Beaumont Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="yourname@beaumont.ie"
                    value={beaumontEmail}
                    onChange={(e) => {
                      setBeaumontEmail(e.target.value);
                      // Reset if they change the email after sending
                      if (codeSent) {
                        setCodeSent(false);
                        setBeaumontOtp('');
                        setBeaumontVerified(false);
                      }
                    }}
                    disabled={beaumontVerified}
                    style={textStyle}
                    isInvalid={beaumontEmail && (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com'))}
                  />
                  <Form.Control.Feedback type="invalid">
                    Must be a @beaumont.ie or @beaumontie.onmicrosoft.com address
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button
                  variant="primary"
                  onClick={handleSendCode}
                  disabled={
                    sendingCode ||
                    (!beaumontEmail.toLowerCase().endsWith('@beaumont.ie') && !beaumontEmail.toLowerCase().endsWith('@beaumontie.onmicrosoft.com')) ||
                    resendCooldown > 0
                  }
                >
                  {sendingCode
                    ? 'Sending…'
                    : codeSent && resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : codeSent
                        ? 'Resend Code'
                        : 'Send Verification Code'}
                </Button>
              </Col>
            </Row>

            {codeSent && (
              <div style={{ marginTop: 16 }}>
                <p style={{ color: '#555', fontSize: 14, marginBottom: 4 }}>
                  Enter the 6-digit code sent to <strong>{beaumontEmail}</strong>:
                </p>
                <OTPInput
                  value={beaumontOtp}
                  onChange={setBeaumontOtp}
                  disabled={confirmingCode || beaumontVerified}
                />
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Button
                    variant="success"
                    onClick={handleConfirmCode}
                    disabled={confirmingCode || beaumontOtp.length !== 6}
                  >
                    {confirmingCode ? 'Verifying…' : 'Confirm Code'}
                  </Button>
                </div>
                <p style={{ color: '#888', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                  Code expires in 10 minutes. Check your spam folder if not received.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Registration form (shown at all times, but submit is blocked until verified) ── */}
      <Form onSubmit={submitHandler}>
        <Form.Group controlId="userType">
          <Form.Label>Registering as</Form.Label>
          <Form.Control as="select" defaultValue="staff" disabled style={textStyle}>
            <option value="staff">Staff Member</option>
          </Form.Control>
        </Form.Group>

        <Row className="mt-3">
          {/* ── Column 1: Personal details + password ── */}
          <Col md={5}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Row>
                <Col sm={2}>
                  <Form.Group controlId="salutation">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      as="select"
                      value={salutation}
                      onChange={(e) => setSalutation(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Professor">Professor</option>
                      <option value="Mx">Mx</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="firstname">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="lastname">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="email" className="mt-2">
                <Form.Label>Work / Login Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={textStyle}
                  required
                />
                <Form.Text className="text-muted">
                  This is your login email — it can be any address.
                </Form.Text>
              </Form.Group>

              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="employeenumber">
                    <Form.Label>Employee Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Employee Number"
                      value={employeenumber}
                      onChange={(e) => setEmployeenumber(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="jobtitle">
                    <Form.Label>Job Title / Category</Form.Label>
                    <Form.Control
                      as="select"
                      value={jobtitle}
                      onChange={(e) => setJobtitle(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select Job Title</option>
                      {CATEGORY_OF_STAFF.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              {/*
              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="effectiveDate">
                    <Form.Label>Effective Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={effectiveDate}
                      readOnly
                      style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Set by administrator after approval
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="reviewDate">
                    <Form.Label>Review Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={reviewDate}
                      readOnly
                      style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Set to 1 year from today by default
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
                    */}

            </div>

            
          </Col>

          {/* ── Column 2: Hospital location ── */}
          <Col md={4}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Form.Group controlId="setDirectorate">
                <Form.Label>Directorate</Form.Label>
                <Form.Control
                  as="select"
                  value={directoratedepartment}
                  onChange={(e) => {
                    setDirectoratedepartment(e.target.value);
                    setSpeciality('');
                    setSpecialityID('');
                    setDirectorateID('');
                  }}
                  style={textStyle}
                >
                  <option value="">Select Directorate</option>
                  {directorates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="wardDepartmentArea" className="mt-3">
                <Form.Label>Ward / Department normally assigned to</Form.Label>
                <Form.Control
                  as="select"
                  value={speciality}
                  onChange={(e) => {
                    const idx         = e.target.selectedIndex;
                    const opts        = e.target.options;
                    const selectedVal = opts[idx].value;
                    const selectedId  = opts[idx].getAttribute('data-id');
                    const selectedDir = opts[idx].getAttribute('data-directorate');
                    setSpeciality(selectedVal);
                    setSpecialityID(selectedId);
                    setDirectorateID(selectedId);
                    if (selectedDir) setDirectoratedepartment(selectedDir);
                  }}
                  disabled={!directoratedepartment}
                  style={textStyle}
                >
                  <option value="">
                    {directoratedepartment ? 'Select Ward / Department' : 'Select a directorate first'}
                  </option>
                  {directoratedepartment &&
                    locationsByDirectorate[directoratedepartment]?.map((loc) => (
                      <option
                        key={loc._id}
                        value={loc.wardDepartmentArea}
                        data-id={loc._id}
                        data-directorate={loc.directorate}
                      >
                        {loc.wardDepartmentArea}
                        {loc.department && ` - ${loc.department}`}
                        {loc.costCentreCode && ` (${loc.costCentreCode})`}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              {specialityID && (
                <div className="mt-2 p-2 bg-light rounded">
                  <small>
                    <strong>Selected:</strong><br />
                    Directorate: {directoratedepartment}<br />
                    Ward/Area: {speciality}
                  </small>
                </div>
              )}
            </div>

            <div className="mt-3">
              <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
                <Form.Group controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Set password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={textStyle}
                    required
                  />
                </Form.Group>
                <Form.Group controlId="confirmPassword" className="mt-2">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={textStyle}
                    required
                  />
                </Form.Group>
              </div>
            </div>
          </Col>

          {/* ── Column 3: Comments + balances ── */}
          <Col md={3}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Form.Group controlId="comment">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Comments"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={textStyle}
                />
                
              </Form.Group>

              {/*
              <Row className="mt-3">
                <Col sm={6}>
                  <Form.Group controlId="openingBalance">
                    <Form.Label>AL Balance (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="bringForward">
                    <Form.Label>Bring Forward (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={bringForward}
                      onChange={(e) => setBringForward(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                
                </Col>
              </Row> */}
              

            </div>
          </Col>
        </Row>

        {/* ── Submit — disabled until beaumont email verified ── */}
        {!beaumontVerified && (
          <p className="mt-3 text-danger" style={{ fontWeight: 500 }}>
            ⚠ You must verify your Beaumont email (Step 1 above) before you can register.
          </p>
        )}

        <Button
          disabled={isLoading || !beaumontVerified}
          type="submit"
          variant="primary"
          className="mt-3"
          title={!beaumontVerified ? 'Verify your Beaumont email first' : ''}
        >
          {isLoading ? 'Registering…' : 'Register'}
        </Button>

        {isLoading && <Loader />}
      </Form>

      <Row className="py-3">
        <Col>
          Already have an account?{' '}
          <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterScreen;